import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import type { ProductFavoriteProps } from '@/components/ProductCard';
import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import { useProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import type { ProductSkuFragment } from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useProcessingLine } from '@/hooks/useProcessingLine';
import { unionProductsAndVariantCta } from '@/integrations/orderApp';
import type { CtaType, Maybe } from '@/lib/types';
import { myAccountMessages } from '@/messages/myAccount';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { StrapProductCard } from '../StrapProductCard/StrapProductCard';
import { WishProductCard } from '../WishProductCard/WishProductCard';

import * as S from '../../WishListView.styles';

export interface ProductWithcompare extends ProductSkuFragment {
  compareType?: boolean;
  variantCTA?: CtaType;
}

export interface WishlistWrapperProps {
  handeChecked: (
    e: React.MouseEvent<HTMLElement>,
    product: ProductWithcompare,
  ) => void;
  handleChange: (product: ProductWithcompare) => void;
  isClosable?: boolean;
  products: ProductWithcompare[];
  removeFavoriteItem: (
    e: { preventDefault: () => void; stopPropagation: () => void },
    product: ProductFavoriteProps,
  ) => void;
}

export const WishlistWrapper = ({
  handeChecked,
  handleChange,
  isClosable = true,
  products,
  removeFavoriteItem,
}: WishlistWrapperProps) => {
  const { query } = useRegionProvider();
  const { isBeingProcessed, setProcessing } = useProcessingLine();
  const { openNotifyMe, setContactUsModalStatus } =
    useCommonModalProviderContext();
  const { formatMessage } = useIntl();
  const { sendViewItemListEvent } = useGoogleAnalytics();
  const {
    lineAdd,
    snapshot: { isProcessing: isCheckoutProcessing },
  } = useCheckout();
  const skus = products
    .map((item) => item.defaultVariant?.sku)
    .flatMap((sku) => (sku ? [sku] : []));

  useEffect(() => {
    const skus = products.map(
      (product) => product.defaultVariant?.sku as string,
    );

    sendViewItemListEvent({
      skus,
      itemListName: 'Wish list',
    });
  }, [products]);

  const { data: orderAppData } = useProductVariantsBusinessDetailsQuery({
    variables: { skus: skus, channel: query.channel },
    notifyOnNetworkStatusChange: true,
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.orderApp,
    }),
  });

  const handleAddToCart = async (
    e: MouseEvent<HTMLElement>,
    id: Maybe<string>,
    sku: Maybe<string>,
    cta: CtaType | undefined,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (cta === 'ORDER' || cta === 'PRE_ORDER') {
      if (id) {
        setProcessing(id);
        await lineAdd(id, cta);
        setProcessing();
      }
    } else if (cta === 'NOTIFY') {
      if (sku) {
        openNotifyMe({ sku });
      }
    } else if (cta === 'NO_CTA') {
      setContactUsModalStatus(true, sku);
    }
  };
  const productsWithVariantCta = unionProductsAndVariantCta(
    orderAppData?.productVariants,
    products,
  );

  return (
    <>
      {productsWithVariantCta && productsWithVariantCta.length > 0 ? (
        <S.WishlistWrapper flexWrap='wrap' justifyContent='center'>
          <S.HitsWrapper itemCount={productsWithVariantCta.length}>
            {productsWithVariantCta.map((product: ProductWithcompare) => {
              return product.productType.slug === 'strap' ? (
                <StrapProductCard
                  key={product.id}
                  addToCart={handleAddToCart}
                  handleChange={handleChange}
                  handleChecked={handeChecked}
                  isCheckoutProcessing={isCheckoutProcessing}
                  isClosable={isClosable}
                  isProcessing={isBeingProcessed(product.id)}
                  product={product}
                  productsLength={products.length}
                  removeFavoriteItem={removeFavoriteItem}
                />
              ) : (
                <WishProductCard
                  key={product.id}
                  addToCart={handleAddToCart}
                  handleChange={handleChange}
                  handleChecked={handeChecked}
                  isCheckoutProcessing={isCheckoutProcessing}
                  isClosable={isClosable}
                  isProcessing={isBeingProcessed(product.id)}
                  product={product}
                  productsLength={products.length}
                  removeFavoriteItem={removeFavoriteItem}
                />
              );
            })}
          </S.HitsWrapper>
        </S.WishlistWrapper>
      ) : (
        <S.Prompt>
          {formatMessage(myAccountMessages.accountWishlistEmptyTitle)}
        </S.Prompt>
      )}
    </>
  );
};
